import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { categorySearchPath } from '../categoryPaths';
import { formatPriceCollectionRange } from '../categoryLabels';
import { PRICE_COLLECTION_TAGLINE } from '../priceCollections';
import { shouldSubmitSearch } from '../searchSubmit';
import {
  breadcrumbJsonLd,
  priceCollectionIntro,
  priceCollectionMetaDescription,
  priceCollectionPageTitle,
} from '../seo';
import { HeaderMoreMenu } from './HeaderMoreMenu';
import { LogoMark } from './LogoMark';
import { CategoryMenuDropdown } from './CategoryMenuDropdown';
import { SearchField } from './SearchField';
import { SearchResultsPage } from './SearchResultsPage';
import { SiteFooter } from './SiteFooter';
import { PageHead } from './PageHead';
import './SearchResultsPage.css';
import './CategoryPage.css';
import './LandingPage.css';

export function PriceCollectionPage() {
  const { slug: rawSlug } = useParams();
  const slug = (rawSlug ?? '').toLowerCase();
  const navigate = useNavigate();

  const [browseCategories, setBrowseCategories] = useState([]);
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [draftQuery, setDraftQuery] = useState('');
  const [scopePillDismissed, setScopePillDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setCollection(null);
    setDraftQuery('');

    Promise.all([
      fetch('/api/landing-categories.php').then((r) => jsonOrThrow(r)),
      fetch('/api/browse-websites.php').then((r) => jsonOrThrow(r)),
    ])
      .then(([landingData, siteData]) => {
        if (cancelled) {
          return;
        }
        const categories = Array.isArray(landingData?.categories)
          ? landingData.categories
          : [];
        const match =
          categories.find((c) => c.slug === slug && c.priceCollection === true)
          ?? null;
        setCollection(match);
        setBrowseCategories(
          Array.isArray(siteData?.categories) ? siteData.categories : [],
        );
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(describeLoadFailure(e));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    setScopePillDismissed(false);
  }, [slug]);

  const collectionName =
    typeof collection?.name === 'string' ? collection.name.trim() : '';
  const priceRangeLabel = collection
    ? formatPriceCollectionRange(collection.priceMin, collection.priceMax)
    : '';

  const canonicalPath = collection ? `/shop/${slug}` : undefined;

  const seoJsonLd = useMemo(() => {
    if (!collectionName || !canonicalPath) {
      return undefined;
    }
    return breadcrumbJsonLd([
      { name: 'Home', url: '/' },
      { name: collectionName, url: canonicalPath },
    ]);
  }, [collectionName, canonicalPath]);

  const embeddedSearch = useMemo(() => {
    if (!collection || scopePillDismissed) {
      return null;
    }
    return {
      shopCategorySlug: slug,
      effectiveQuery: '',
      priceCollection: collection,
    };
  }, [collection, slug, scopePillDismissed]);

  function applySearch(query) {
    const trimmed = query.trim();

    if (scopePillDismissed) {
      if (!shouldSubmitSearch(trimmed, {})) {
        return;
      }
      navigate(categorySearchPath(trimmed, ''));
      return;
    }

    if (!collection) {
      return;
    }
    if (!shouldSubmitSearch(trimmed, { shopSlug: slug })) {
      return;
    }
    navigate(categorySearchPath(trimmed, slug));
  }

  return (
    <div className="results-page category-page subcategory-page price-collection-page">
      {collectionName && canonicalPath && (
        <PageHead
          title={priceCollectionPageTitle(collectionName)}
          description={priceCollectionMetaDescription(collectionName)}
          canonicalPath={canonicalPath}
          jsonLd={seoJsonLd}
        />
      )}
      <header className="results-page__header">
        <div className="results-page__header-inner">
          <div className="results-page__header-left">
            <LogoMark variant="header" />
            <CategoryMenuDropdown
              browseCategories={browseCategories}
              activeShopCategorySlug={slug}
              disabled={loading}
            />
            <HeaderMoreMenu />
          </div>
          <div className="results-page__header-center">
            <SearchField
              id="price-collection-search"
              className="results-page__search"
              value={draftQuery}
              onChange={setDraftQuery}
              onSubmitSearch={applySearch}
              searchSubmitScope={
                scopePillDismissed ? {} : { shopSlug: slug }
              }
              placeholder={
                scopePillDismissed
                  ? 'Search products…'
                  : 'Search in this category…'
              }
              gaSearchLocation="price_collection_page"
              scopePill={
                collectionName && !scopePillDismissed
                  ? {
                      label: collectionName,
                      onRemove: () => setScopePillDismissed(true),
                    }
                  : null
              }
            />
          </div>
          <nav
            className="results-page__header-nav"
            aria-label="Site sections"
          >
            <Link className="results-page__nav-link" to="/about">
              About
            </Link>
            <Link className="results-page__nav-link" to="/add-store">
              Add your store
            </Link>
          </nav>
        </div>
      </header>

      <div className="category-page__main subcategory-page__intro-wrap">
        {loading && (
          <p className="category-page__status" aria-live="polite">
            Loading…
          </p>
        )}
        {!loading && loadError && (
          <p className="category-page__status category-page__status--error" role="alert">
            {loadError}
          </p>
        )}
        {!loading && !loadError && collection == null && (
          <div className="category-page__not-found">
            <p className="category-page__status">Category not found.</p>
            <Link className="category-page__back" to="/">
              Back to home
            </Link>
          </div>
        )}
        {!loading && !loadError && collection != null && (
          <>
            <div className="price-collection-page__title-wrap">
              <span className="landing-categories__price-badge">By price</span>
              <h1 className="category-page__title">{collectionName} in Malta</h1>
              {priceRangeLabel && (
                <div className="price-collection-page__range-row">
                  <p className="price-collection-page__range">{priceRangeLabel}</p>
                  <p className="price-collection-page__tagline">
                    {PRICE_COLLECTION_TAGLINE}
                  </p>
                </div>
              )}
            </div>
            <p className="category-page__intro">{priceCollectionIntro(collectionName)}</p>
          </>
        )}
      </div>

      {embeddedSearch && (
        <SearchResultsPage embedded={embeddedSearch} />
      )}

      <SiteFooter
        browseCategories={browseCategories}
        activeShopCategorySlug={slug}
      />
    </div>
  );
}
